// Morrow — Workflow SETUP page (the "next step" after Create). Reached at
// /workflows/$id. Based on the workflow's tools + triggers, the AI drafts what it
// will actually DO, the user refines it, saves, and can test-run it — so a created
// workflow becomes a working one instead of an inert row.
import {
  ArrowLeftIcon,
  DownloadIcon,
  FileTextIcon,
  Loader2Icon,
  PlayIcon,
  SparklesIcon,
  XIcon,
  ZapIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearch } from "@tanstack/react-router";

import { apiUrl } from "../../apiBase";
import { maybeOpenUpgradeFrom402 } from "../../upgradeModal";
import { resolveComposioUserId } from "../../composioConnect";
import { fetchVaultFolders, fetchVaultItems } from "../../morrowVault";
import { useI18n, type Translator } from "~/i18n";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { TriggerComposer, createTriggerFor, triggerSourceOf } from "./WorkflowsPage";
import { cronToHumanLocalized, runDetailLabel } from "./scheduleI18n";
import { branchInstructionLines, describeFlow, graphHasBranching } from "./workflowGraphText";
import { hasRoutes, nodeAppSlugs, routeIdFromLabel } from "./workflowRoutes";
import { workflowMatchPhrase } from "./workflowMatchPhrases";
import { downloadWorkflowJson } from "./workflowTransfer";
import {
  WorkflowCanvas,
  type WfApp,
  type WfForEachConfig,
  type WfGraph,
  type WfNodeData,
} from "./WorkflowCanvas";
import { RunStepsPanel } from "./RunStepsPanel";
import { canvasKindLabel } from "./workflowCanvasI18n";
import type { Edge, Node } from "@xyflow/react";

/** One step the workflow maker proposes. Mirrors ComposedStep in the server's workflowCompose.ts;
 * the maker is told the engine's rules there, and these are the fields it answers with. */
type ComposedStep = {
  type: "app" | "browser" | "vault" | "decision" | "foreach" | "output";
  /** foreach: the list, the one step per item, and where results go. */
  forEach?: ComposedForEach;
  /** foreach v2 (container): per-item body sub-flow; list from the step before, results to after. */
  body?: ComposedStep[];
  concurrency?: number;
  staggerSeconds?: number;
  onFail?: "skip" | "stop";
  app?: string;
  /** Several toolkits on ONE step, when it genuinely needs more than one. */
  apps?: string[];
  /** "all" = needs every listed app (default); "any" = one of them is enough. */
  appMode?: "all" | "any";
  /** decision: named exits. `goTo` is a 1-based step number to jump BACK to (the loop),
   * "next" to carry on, or "end" to stop. */
  routes?: Array<{
    label: string;
    when: { kind: "hasMoreItems" | "contains" | "notContains" | "ask" | "always"; value?: string };
    goTo: number | "next" | "end";
  }>;
  /** decision: how many times round a loop before it gives up and reports. */
  maxPasses?: number;
  actions?: string[];
  text?: string;
  /** output step: channel to send on (e.g. "personal-whatsapp", "whatsapp", "telegram"). */
  channel?: string;
  /** output step: keep replying / working the target instead of a one-shot. */
  autonomous?: boolean;
};
/** Mirrors ComposedForEach in the server's workflowCompose.ts. */
type ComposedForEach = {
  sheet?: string;
  tab?: string;
  fromStep?: number;
  skipWhere?: { column: string; equals: string };
  do: { kind: "browser" | "app" | "ai"; text: string };
  atOnce?: number;
  secondsApart?: number;
  onFail?: "skip" | "stop";
  saveTo?: { resultColumn: string; noteColumn?: string };
};
/** A composed foreach → the canvas block's settings (mirror of forEachConfigFromComposed). */
function forEachConfigFromComposed(
  fe: ComposedForEach,
  stepIdAt: (stepNumber: number) => string | undefined,
): WfForEachConfig {
  const fromId = fe.fromStep !== undefined ? stepIdAt(fe.fromStep) : undefined;
  return {
    source: fromId
      ? { kind: "step", stepId: fromId }
      : { kind: "sheet", spreadsheetId: fe.sheet ?? "", sheet: fe.tab ?? "" },
    ...(fe.skipWhere
      ? { filter: { column: fe.skipWhere.column, notEquals: fe.skipWhere.equals } }
      : {}),
    concurrency: Math.min(5, Math.max(1, Math.floor(fe.atOnce ?? 3))),
    staggerSeconds: Math.min(600, Math.max(0, Math.floor(fe.secondsApart ?? 20))),
    onFail: fe.onFail ?? "skip",
    body: { kind: fe.do.kind, text: fe.do.text },
    ...(fe.saveTo
      ? {
          saveTo: {
            outcomeColumn: fe.saveTo.resultColumn,
            ...(fe.saveTo.noteColumn ? { noteColumn: fe.saveTo.noteColumn } : {}),
          },
        }
      : {}),
  };
}
type ComposeRes = {
  needsImage?: boolean;
  instruction?: string;
  summary?: string;
  reply?: string;
  questions?: string[];
  triggers?: ComposedTrigger[];
  steps?: ComposedStep[];
};
/** The reviewed-before-build plan from /api/morrow/workflows/plan. */
type WfPlan = {
  goal?: string;
  understanding?: string;
  apps?: string[];
  capabilities?: string[];
  triggerSummary?: string;
  outline?: string[];
  questions?: string[];
  reply?: string;
};
type ChatMsg = { role: "user" | "assistant"; content: string };

/** Per-workflow store for the "Edit with AI" chat, so the thread outlives a panel close /
 * navigation / reload. Browser-only convenience — every access is guarded (private windows,
 * blocked storage, or a bad JSON blob must never break the builder). */
const chatStoreKey = (wfId: string) => `morrow.wf.chat.${wfId}`;
function readChatMessages(wfId: string): ChatMsg[] {
  try {
    const raw = localStorage.getItem(chatStoreKey(wfId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed)
      ? parsed.filter(
          (m): m is ChatMsg =>
            !!m &&
            typeof (m as ChatMsg).content === "string" &&
            ((m as ChatMsg).role === "user" || (m as ChatMsg).role === "assistant"),
        )
      : [];
  } catch {
    return [];
  }
}
function writeChatMessages(wfId: string, msgs: ChatMsg[]): void {
  try {
    if (msgs.length) localStorage.setItem(chatStoreKey(wfId), JSON.stringify(msgs));
    else localStorage.removeItem(chatStoreKey(wfId));
  } catch {
    /* storage unavailable — the in-memory thread still works for this session */
  }
}
type ComposedTrigger = {
  kind: "whatsapp" | "comms" | "app-event" | "schedule" | "chat" | "vault-item-added";
  channel?: string;
  matchKind?: "all" | "contact" | "keyword";
  matchValue?: string;
  toolkit?: string;
  triggerSlug?: string;
  cron?: string;
  vaultFolderId?: string;
  vaultFolderName?: string;
};

/**
 * The chip for a vault-folder trigger.
 *
 * `folderName` is optional because a saved row only stores the folder id — when the name hasn't
 * been resolved yet the id is shown rather than an empty "Vault ·", which would read as broken.
 */
function vaultFolderLabel(t: Translator, folderName: string | undefined | null): string {
  return t("workflows.composer.vault.chip", {
    folder: folderName || t("workflows.composer.vault.unknownFolder"),
  });
}

function labelForTrigger(t: Translator, trig: ComposedTrigger): string {
  const phrase = workflowMatchPhrase(trig.matchKind ?? "all", trig.matchValue ?? "", t);
  if (trig.kind === "whatsapp") return `${t("workflows.setup.myWhatsapp")} · ${phrase}`;
  if (trig.kind === "comms") return `${prettify(trig.channel ?? "")} · ${phrase}`;
  if (trig.kind === "app-event")
    return `${prettify(trig.toolkit ?? "")} · ${trig.triggerSlug ?? t("workflows.setup.eventFallback")}`;
  if (trig.kind === "schedule") return cronToHumanLocalized(trig.cron ?? "", t);
  if (trig.kind === "vault-item-added") return vaultFolderLabel(t, trig.vaultFolderName);
  return t("workflows.setup.fromChat");
}

// AI "app" slugs that are really the file Vault (not a Composio app) → render as a Vault node.
const VAULT_APP_SLUGS = new Set(["vault", "files", "file", "documents", "docs"]);

/** Turn the AI's ordered steps into a canvas graph (triggers → steps → output). */
function buildGraphFromSteps(
  triggerLabels: string[],
  steps: ComposedStep[],
  t: Translator,
  needsImage = false,
): WfGraph {
  let seq = 0;
  const mk = () => `n_ai_${Date.now().toString(36)}_${(seq += 1)}`;
  const rf = (k: WfNodeData["kind"]) =>
    k === "trigger"
      ? "wfTrigger"
      : k === "app"
        ? "wfApp"
        : k === "browser"
          ? "wfBrowser"
          : k === "vault"
            ? "wfVault"
            : k === "decision"
              ? "wfDecision"
              : k === "foreach"
                ? "wfForEach"
                : "wfOutput";
  const nodes: Node<WfNodeData>[] = [];
  const edges: Edge[] = [];
  /** step index → node id, so a route can point at a step by number. */
  const stepIds: Array<string | undefined> = [];

  const trigLabels = triggerLabels.length ? triggerLabels : [t("workflows.setup.fromChatTrigger")];
  let prev: string[] = trigLabels.map((l, i) => {
    const id = mk();
    nodes.push({
      id,
      type: rf("trigger"),
      position: { x: 0, y: i * 130 },
      data: { kind: "trigger", label: l, ...(needsImage ? { requires: ["image"] } : {}) },
    });
    return id;
  });

  // A For each flow reports by itself (the engine writes the tally), so it gets no default output.
  const hasOutput = steps.some((s) => s.type === "output" || s.type === "foreach");
  const seqSteps: ComposedStep[] = hasOutput
    ? steps
    : [...steps, { type: "output", text: t("workflows.setup.defaultOutputText") }];

  seqSteps.forEach((s, i) => {
    const id = mk();
    // An AI "app" step pointing at the file vault (app: "vault"/"files"/…) is really a
    // Vault node — coerce it so the user gets the file checkboxes, not a dead app card.
    const isVaultApp = s.type === "app" && !!s.app && VAULT_APP_SLUGS.has(s.app.toLowerCase());
    const data: WfNodeData =
      s.type === "vault" || isVaultApp
        ? { kind: "vault", label: canvasKindLabel("vault", t), vaultIds: [] }
        : s.type === "app"
          ? {
              kind: "app",
              label: s.app ? prettify(s.app) : canvasKindLabel("app", t),
              // Carry the LIST the maker may have given; `appSlug` stays for back-compat.
              appSlug: s.app ?? "",
              ...(s.apps && s.apps.length > 1
                ? { appSlugs: [...s.apps], appMode: s.appMode ?? "all" }
                : {}),
              steps: s.actions?.length ? [...s.actions] : [""],
            }
          : s.type === "browser"
            ? { kind: "browser", label: canvasKindLabel("browser", t), text: s.text ?? "" }
            : s.type === "foreach" && s.body
              ? {
                  kind: "foreach",
                  label: canvasKindLabel("foreach", t),
                  ...(s.concurrency !== undefined ? { concurrency: s.concurrency } : {}),
                  ...(s.staggerSeconds !== undefined ? { staggerSeconds: s.staggerSeconds } : {}),
                  ...(s.onFail ? { onFail: s.onFail } : {}),
                }
              : s.type === "foreach" && s.forEach
                ? {
                    kind: "foreach",
                    label: canvasKindLabel("foreach", t),
                    forEachConfig: forEachConfigFromComposed(s.forEach, (n) => stepIds[n - 1]),
                  }
                : s.type === "decision"
                  ? {
                      kind: "decision",
                      label: canvasKindLabel("decision", t),
                      text: s.text ?? "",
                      // Routes make it an A-or-B with real ports; without them it is the old gate.
                      ...(s.routes && s.routes.length > 0
                        ? {
                            routes: s.routes.map((r, ri) => ({
                              id: routeIdFromLabel(
                                r.label,
                                s.routes!.slice(0, ri).map((x) => x.label),
                              ),
                              label: r.label,
                              when:
                                r.when.kind === "contains" || r.when.kind === "notContains"
                                  ? { kind: r.when.kind, value: r.when.value ?? "" }
                                  : r.when.kind === "ask"
                                    ? { kind: "ask" as const, text: r.when.value ?? "" }
                                    : r.when.kind === "hasMoreItems"
                                      ? { kind: "hasMoreItems" as const }
                                      : { kind: "always" as const },
                            })),
                            maxPasses: s.maxPasses ?? 50,
                          }
                        : {}),
                    }
                  : {
                      kind: "output",
                      label: canvasKindLabel("output", t),
                      text: s.text ?? "",
                      channel: s.channel ?? "",
                      autonomous: s.autonomous ?? false,
                    };
    nodes.push({
      id,
      type: rf(data.kind),
      position: { x: (i + 1) * 320, y: 40 },
      data,
      ...(data.kind === "foreach" ? { style: { width: 420, height: 380 } } : {}),
    });
    stepIds[i] = id;
    for (const p of prev)
      edges.push({ id: `e_${p}_${id}`, source: p, target: id, type: "insert", animated: true });
    // A v2 For each is a container: emit its body steps as children (parentId), chained.
    if (s.type === "foreach" && s.body && s.body.length > 0) {
      let childPrev: string | null = null;
      s.body.forEach((b, bi) => {
        const cid = mk();
        const cData: WfNodeData =
          b.type === "app"
            ? {
                kind: "app",
                label: b.app ? prettify(b.app) : canvasKindLabel("app", t),
                appSlug: b.app ?? "",
                steps: b.actions?.length ? [...b.actions] : [""],
              }
            : b.type === "output"
              ? { kind: "output", label: canvasKindLabel("output", t), text: b.text ?? "" }
              : { kind: "browser", label: canvasKindLabel("browser", t), text: b.text ?? "" };
        nodes.push({
          id: cid,
          type: rf(cData.kind),
          position: { x: 24, y: 164 + bi * 168 },
          data: cData,
          parentId: id,
          extent: "parent",
        });
        if (childPrev)
          edges.push({
            id: `e_${childPrev}_${cid}`,
            source: childPrev,
            target: cid,
            type: "insert",
            animated: true,
          });
        childPrev = cid;
      });
    }
    // A routing step's exits are wired below, once every step has an id — a route may point BACK.
    prev = data.kind === "decision" && hasRoutes(data.routes) ? [] : [id];
  });

  // Routes, now that every step id exists. `goTo` is a 1-based step number to jump back to (the
  // loop), "next" to carry on, or "end" to stop — which is how a linear step list from the maker
  // can describe a graph that branches and repeats.
  seqSteps.forEach((s, i) => {
    if (s.type !== "decision" || !s.routes?.length) return;
    const from = stepIds[i];
    if (!from) return;
    const routeIds = (nodes.find((n) => n.id === from)?.data.routes ?? []).map((r) => r.id);
    s.routes.forEach((route, ri) => {
      const handle = routeIds[ri];
      if (!handle) return;
      const target =
        typeof route.goTo === "number"
          ? stepIds[route.goTo - 1]
          : route.goTo === "next"
            ? stepIds[i + 1]
            : undefined;
      if (!target || target === from) return;
      edges.push({
        id: `e_${from}_${handle}_${target}`,
        source: from,
        target,
        sourceHandle: handle,
        type: "insert",
      });
    });
  });
  return { nodes, edges };
}

type Workflow = {
  id: string;
  name: string;
  instruction: string;
  allowedApps: string[] | null;
  capabilities: Record<string, unknown> | null;
  autonomy: string | null;
  graph: WfGraph | null;
  // Whether the workflow is enabled (returned by the list endpoint as enabled===1).
  // Preserve it on update — hardcoding true silently re-enabled paused workflows.
  enabled: boolean;
};
type WaTrigger = { id: string; matchKind: string; matchValue: string; workflowId: string | null };
type WfTrigger = {
  id: string;
  workflowId: string;
  kind: string;
  channel: string | null;
  toolkit: string | null;
  triggerSlug: string | null;
  matchKind: string;
  matchValue: string;
  scheduleCron: string | null;
  /** For `vault-item-added`: the vault folder being watched. */
  vaultFolderId?: string | null;
};

const BTN =
  "inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60";
const GHOST =
  "inline-flex items-center justify-center gap-1.5 rounded-md border border-border/60 px-3 py-1.5 text-sm font-medium text-muted-foreground transition hover:bg-muted/50 hover:text-foreground disabled:opacity-60";
/**
 * A compact icon-only action for the canvas top bar.
 *
 * These used to be full `GHOST` buttons with text labels, which put six padded pills across the
 * top-right corner of a canvas they float over — wider than the workflow's own title. Icon-only
 * at the same 28px as the list card's toolbar keeps the canvas visible underneath; every one
 * carries a title AND an aria-label, so dropping the visible text costs nothing to a screen
 * reader or to a hover.
 */
const ICON_ACTION =
  "pointer-events-auto inline-flex size-7 shrink-0 items-center justify-center rounded-md border border-border/60 bg-background/90 text-muted-foreground shadow-sm backdrop-blur transition hover:bg-muted/60 hover:text-foreground disabled:opacity-60";

const INPUT =
  "w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40";

async function getJson<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(apiUrl(path), { headers: { accept: "application/json" } });
    return res.ok ? ((await res.json()) as T) : null;
  } catch {
    return null;
  }
}
async function postJson<T = unknown>(
  path: string,
  body: Record<string, unknown>,
): Promise<T | null> {
  try {
    const res = await fetch(apiUrl(path), {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
    if (await maybeOpenUpgradeFrom402(res)) return null;
    return res.ok ? ((await res.json()) as T) : null;
  } catch {
    return null;
  }
}
/**
 * Like `postJson`, but keeps the server's message when a route refuses ON PURPOSE —
 * e.g. a 409 from running a workflow that already has a run in flight. `postJson`
 * throws that body away, so the caller can only say "couldn't run it", which reads as
 * a bug rather than as the answer it is.
 */
async function postJsonResult<T = unknown>(
  path: string,
  body: Record<string, unknown>,
): Promise<{ readonly data: T | null; readonly error: string | null }> {
  try {
    const res = await fetch(apiUrl(path), {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
    if (await maybeOpenUpgradeFrom402(res)) return { data: null, error: null };
    if (res.ok) return { data: (await res.json()) as T, error: null };
    const refused = (await res.json().catch(() => null)) as { error?: string } | null;
    return { data: null, error: refused?.error ?? null };
  } catch {
    return { data: null, error: null };
  }
}

function prettify(slug: string): string {
  return slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : slug;
}

// Built-in sources that are NEVER a connectable Composio app — the run always has them,
// so they must not show up in the "connect X to run this" banner.
const BUILTIN_SOURCES = new Set(["vault", "files", "file", "personal-whatsapp", "chat", "comms"]);

/**
 * Merge the capability toggles a graph's nodes imply into a capability object — the web
 * mirror of the server's workflowCapabilities.ensureGraphCapabilities, so what the UI saves
 * and shows agrees with what the server stores without a reload. A node on the canvas IS the
 * grant: a browser / cloud-browser / vault node must turn its capability ON. Only ever ON.
 */
function capsForGraph(
  g: WfGraph | null,
  base: Record<string, unknown> | null,
): Record<string, unknown> {
  const out = { ...(base ?? {}) };
  for (const n of g?.nodes ?? []) {
    const kind = (n.data as WfNodeData)?.kind;
    if (kind === "browser") out.browser = true;
    if (kind === "cloudBrowser") {
      out.browser = true;
      out.cloudBrowser = true;
    }
    if (kind === "vault") out.vault = true;
  }
  return out;
}

/** Does an app-step's slug need connecting? Built-ins are always available; a plain
 * "whatsapp" is satisfied by either the personal-WhatsApp link or a WhatsApp comms app. */
function appNeedsConnecting(slug: string, apps: WfApp[]): boolean {
  if (!slug || BUILTIN_SOURCES.has(slug)) return false;
  if (slug === "whatsapp")
    return !apps.some((a) => a.slug === "personal-whatsapp" || a.slug === "whatsapp");
  return !apps.some((a) => a.slug === slug);
}

/**
 * Rewrite the graph's trigger-node label(s) to match the live trigger rows.
 *
 * Triggers are backend rows, not graph nodes — adding/removing one updates the rows and the
 * Triggers-panel chips, but the canvas trigger node kept its label frozen from seed time until
 * an AI recompose or a full reload (the "editing a trigger does nothing" bug). This syncs the
 * node text in place without touching edges: when the node count matches the labels we map
 * one-to-one; otherwise we fold the labels into a single summary on the first trigger node so
 * no edge is ever orphaned. Returns the same reference when nothing changed (no needless redraw).
 */
function syncTriggerLabels(g: WfGraph | null, labels: string[], fallback: string): WfGraph | null {
  if (!g) return g;
  const desired = labels.length ? labels : [fallback];
  const trigNodes = g.nodes.filter((n) => (n.data as WfNodeData).kind === "trigger");
  if (trigNodes.length === 0) return g;
  const summary = desired.join(" · ");
  const oneToOne = trigNodes.length === desired.length;
  let ti = 0;
  let changed = false;
  const nodes = g.nodes.map((n) => {
    if ((n.data as WfNodeData).kind !== "trigger") return n;
    const nextLabel = (oneToOne ? desired[ti++] : summary) ?? summary;
    if ((n.data as WfNodeData).label === nextLabel) return n;
    changed = true;
    return { ...n, data: { ...(n.data as WfNodeData), label: nextLabel } };
  });
  return changed ? { nodes, edges: g.edges } : g;
}

type Run = {
  id: string;
  workflowId: string;
  source: string;
  detail: string | null;
  status: string;
  error: string | null;
  createdAt: string;
  /** The run's own conversation, so its row can jump straight into it. */
  threadId: string | null;
};

export function WorkflowSetupPage() {
  const { t } = useI18n();
  const { id } = useParams({ from: "/workflows/$id" });
  const search = useSearch({ from: "/workflows/$id" });
  const [tab, setTab] = useState<"overview" | "edit">(search.tab === "edit" ? "edit" : "overview");
  const { isLoaded, isSignedIn, userId: clerkUserId } = useMorrowClerkAuth();
  const userId = useMemo(
    () => resolveComposioUserId(isLoaded && isSignedIn ? clerkUserId : null),
    [isLoaded, isSignedIn, clerkUserId],
  );
  const [wf, setWf] = useState<Workflow | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [triggerLabels, setTriggerLabels] = useState<string[]>([]);
  /** Set when an AI edit would have flattened a branching canvas, so we kept the canvas. */
  const [branchKeptNotice, setBranchKeptNotice] = useState(false);
  const [waRows, setWaRows] = useState<WaTrigger[]>([]);
  const [wfRows, setWfRows] = useState<WfTrigger[]>([]);
  /** vault folder id → name, so a folder trigger reads as its folder and not a raw id. Only
   * fetched when this workflow actually has a folder trigger. */
  const [vaultFolderNames, setVaultFolderNames] = useState<Record<string, string>>({});
  const [runs, setRuns] = useState<Run[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [graph, setGraph] = useState<WfGraph | null>(null);
  const [apps, setApps] = useState<WfApp[]>([]);
  const [vaultItems, setVaultItems] = useState<{ id: string; name: string }[]>([]);
  /**
   * Which bottom-left panel is open — at most one.
   *
   * Triggers and the instruction used to share one "Triggers & instruction" popover, which meant
   * the instruction — the longest-form, most-edited thing on this page — was a 60vh textarea
   * buried under the trigger picker inside a 60vh scroll box. They are separate concerns with
   * separate shapes, so each gets its own panel and its own full height.
   */
  const [panel, setPanel] = useState<"none" | "triggers" | "instruction">("none");
  const [canvasKey, setCanvasKey] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const [rightTab, setRightTab] = useState<"chat" | "runs">("chat");
  // The "Edit with AI" conversation persists per workflow (localStorage): closing the panel,
  // leaving the builder, or reloading kept losing the whole thread before — now it survives.
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>(() => readChatMessages(id));
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);

  const [intent, setIntent] = useState("");
  const [instruction, setInstruction] = useState("");
  // True once the user has hand-edited the instruction textarea. When set, Save
  // persists their exact text instead of recompiling it from the graph (which
  // otherwise silently discards manual edits — the compiled instruction starts
  // with "Run this as an ordered…" and buildExecInstruction skips folding a note
  // that looks like an already-compiled instruction). Programmatic sets (load,
  // AI build/compose) reset it to false.
  const [instructionDirty, setInstructionDirty] = useState(false);
  const [summary, setSummary] = useState("");
  const [questions, setQuestions] = useState<string[]>([]);
  const [drafting, setDrafting] = useState(false);
  // Reason → review → build (for a freshly-created workflow): the cleaned plan the
  // user approves before the flow is built, plus the two loading phases.
  const [plan, setPlan] = useState<WfPlan | null>(null);
  const [planning, setPlanning] = useState(false);
  const [building, setBuilding] = useState(false);
  // Plan review is a CHAT: the seed intent, the running conversation (it can ask
  // questions, you answer, it refines), and the input — until you approve → build.
  const [planIntent, setPlanIntent] = useState("");
  const [planInput, setPlanInput] = useState("");
  const [planChat, setPlanChat] = useState<{ role: "user" | "assistant"; content: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    const [list, wa, wtr, rn] = await Promise.all([
      // ONE workflow by id, not the whole list. This used to fetch every workflow with its full
      // graph and keep one, so opening the builder (and every save-then-reload) cost N graphs.
      getJson<{ workflows?: Workflow[] }>(`/api/morrow/workflows?id=${encodeURIComponent(id)}`),
      getJson<{ triggers?: WaTrigger[] }>("/api/morrow/whatsapp-triggers"),
      getJson<{ triggers?: WfTrigger[] }>("/api/morrow/workflow-triggers"),
      getJson<{ runs?: Run[] }>("/api/morrow/workflows/runs"),
    ]);
    const found = (list?.workflows ?? []).find((w) => w.id === id) ?? null;
    if (!found) {
      setNotFound(true);
      return [];
    }
    setWf(found);
    setInstruction(found.instruction ?? "");
    setInstructionDirty(false);
    // (graph is set below, after trigger labels are computed, so the canvas trigger node
    // reflects the live trigger rows on load — not a label frozen from when it was saved.)
    const wr = (wa?.triggers ?? []).filter((t) => t.workflowId === id);
    const fr = (wtr?.triggers ?? []).filter((t) => t.workflowId === id);
    setWaRows(wr);
    setWfRows(fr);
    setRuns((rn?.runs ?? []).filter((r) => r.workflowId === id));
    // Resolve folder names only if a folder trigger is actually wired up.
    let folderNames: Record<string, string> = {};
    if (fr.some((row) => row.kind === "vault-item-added")) {
      folderNames = Object.fromEntries((await fetchVaultFolders()).map((f) => [f.id, f.name]));
      setVaultFolderNames(folderNames);
    }
    const labels: string[] = [];
    for (const row of wr) {
      labels.push(
        `${t("workflows.setup.myWhatsapp")} · ${workflowMatchPhrase(row.matchKind, row.matchValue, t)}`,
      );
    }
    for (const row of fr) {
      if (row.kind === "comms") {
        labels.push(
          `${prettify(row.channel ?? "")} · ${workflowMatchPhrase(row.matchKind, row.matchValue, t)}`,
        );
      } else if (row.kind === "app-event") {
        labels.push(`${prettify(row.toolkit ?? "")} · ${row.triggerSlug}`);
      } else if (row.kind === "vault-item-added") {
        labels.push(vaultFolderLabel(t, folderNames[row.vaultFolderId ?? ""]));
      } else {
        labels.push(cronToHumanLocalized(row.scheduleCron ?? "", t));
      }
    }
    setGraph(syncTriggerLabels(found.graph ?? null, labels, t("workflows.setup.fromChatTrigger")));
    setTriggerLabels(labels);
    return labels;
  }, [id, t]);

  // After a trigger row changes, reflect the new labels on the canvas trigger node immediately
  // (and bump the key so the node remounts with the fresh text) instead of waiting for a reload.
  const syncCanvasTriggers = useCallback(
    (labels: string[]) => {
      setGraph((g) => syncTriggerLabels(g, labels, t("workflows.setup.fromChatTrigger")));
      setCanvasKey((k) => k + 1);
    },
    [t],
  );

  const addTrigger = useCallback(
    async (p: Parameters<typeof createTriggerFor>[1]) => {
      await createTriggerFor(id, p);
      syncCanvasTriggers(await load());
    },
    [id, load, syncCanvasTriggers],
  );
  const removeWa = useCallback(
    async (rid: string) => {
      await postJson("/api/morrow/whatsapp-triggers/delete", { id: rid });
      syncCanvasTriggers(await load());
    },
    [load, syncCanvasTriggers],
  );
  const removeWf = useCallback(
    async (rid: string) => {
      await postJson("/api/morrow/workflow-triggers/delete", { id: rid });
      syncCanvasTriggers(await load());
    },
    [load, syncCanvasTriggers],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Persist the AI chat per workflow. The reload effect is deliberately defined AFTER the save
  // effect: on an in-place id change the save effect (still holding the previous id) runs first
  // and bails, so it can never write the old thread onto the new workflow's key before reload
  // swaps the messages in.
  const chatPersistId = useRef(id);
  useEffect(() => {
    if (chatPersistId.current !== id) return; // id just changed — let the reload effect run first
    writeChatMessages(id, chatMessages);
  }, [chatMessages, id]);
  useEffect(() => {
    if (chatPersistId.current === id) return; // same workflow — initializer already hydrated it
    chatPersistId.current = id;
    setChatMessages(readChatMessages(id));
  }, [id]);

  // Connected apps power the per-node app picker in the canvas. We include the user's
  // personal WhatsApp (Baileys, not a Composio toolkit) as a first-class source so a
  // step can target it distinctly from WhatsApp Business.
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    void Promise.all([
      getJson<{ connected?: string[] }>(
        `/api/morrow/composio/connections?userId=${encodeURIComponent(userId)}`,
      ),
      getJson<{ status?: string }>("/api/morrow/whatsapp-personal/status"),
      // Built-in apps (DIA, Entegra, Trendyol, …) live in OUR database, NOT Composio, so the
      // Composio connected-set can't see them. Without this the builder judged every built-in
      // against the Composio list alone and showed a connected DIA/Entegra as "not connected"
      // (the server run path already exempts them — see workflowRun.isComposioAdjudicable).
      getJson<{ connections?: { appId?: string; status?: string }[] }>(
        "/api/morrow/apps/connections",
      ),
      // Our COMPANY comms channels (official WhatsApp Business / Telegram / … set up in
      // Settings → Messaging). Offered as a pickable "comms" app so a workflow step can
      // message the person on our channel and wait for their reply (ask_on_comms tool).
      getJson<{ channels?: { channel?: string; connected?: boolean }[] }>(
        "/api/morrow/comms/status",
      ),
    ]).then(([conn, wa, builtin, comms]) => {
      if (!alive) return;
      const composio = conn && Array.isArray(conn.connected) ? conn.connected : [];
      // A stored row means they connected it; an error row is still "configured", so don't nag.
      const builtinSlugs = (builtin?.connections ?? [])
        .map((c) => (typeof c.appId === "string" ? c.appId : ""))
        .filter((s) => s);
      const seen = new Set<string>();
      const list: WfApp[] = [];
      for (const slug of [...composio, ...builtinSlugs]) {
        if (!slug || seen.has(slug)) continue;
        seen.add(slug);
        list.push({ slug, name: prettify(slug) });
      }
      const personalOn = wa?.status === "connected";
      // Always offer personal WhatsApp as a distinct, pickable source (its own slug so a
      // send/act step targets the linked-device account, not WhatsApp Business).
      list.unshift({
        slug: "personal-whatsapp",
        name: personalOn ? "WhatsApp (personal)" : "WhatsApp (personal · link it)",
        personal: true,
      });
      // Our company comms channel — distinct from the user's personal WhatsApp. A step
      // scoped here (or told to "ask on comms") messages the person on OUR official
      // WhatsApp Business / channel and waits for their reply. Always offered so it's
      // visible; labelled when it still needs setting up in Settings → Messaging.
      const commsOn = (comms?.channels ?? []).some((c) => c.connected === true);
      list.unshift({
        slug: "comms",
        name: commsOn ? "Comms — our WhatsApp" : "Comms — our WhatsApp (set up in Messaging)",
      });
      setApps(list);
    });
    return () => {
      alive = false;
    };
  }, [userId]);

  // Vault files power the Vault node's file picker.
  useEffect(() => {
    let alive = true;
    void fetchVaultItems().then((items) => {
      if (alive) setVaultItems(items.map((v) => ({ id: v.id, name: v.name })));
    });
    return () => {
      alive = false;
    };
  }, []);

  const capList = useMemo(() => {
    const c = wf?.capabilities ?? {};
    const on: string[] = [];
    if (c.browser) on.push("browser");
    if (c.memoryRead) on.push("read memory");
    if (c.memoryWrite) on.push("write memory");
    if (c.vault) on.push("files/vault");
    return on;
  }, [wf]);

  const capListDisplay = useMemo(() => {
    const c = wf?.capabilities ?? {};
    const on: string[] = [];
    if (c.browser) on.push(t("workflows.new.cap.browser"));
    if (c.memoryRead) on.push(t("workflows.new.cap.memoryRead"));
    if (c.memoryWrite) on.push(t("workflows.new.cap.memoryWrite"));
    if (c.vault) on.push(t("workflows.new.cap.vault"));
    return on;
  }, [wf, t]);

  // What the built flow needs but isn't set up yet — apps referenced but not connected,
  // or app steps with no app chosen. Surfaced so the user knows what to add to run it.
  const missing = useMemo(() => {
    const need = new Set<string>();
    let needsPick = false;
    for (const n of graph?.nodes ?? []) {
      const d = n.data as WfNodeData;
      if (d.kind === "app") {
        const slugs = nodeAppSlugs(d);
        if (slugs.length === 0) needsPick = true;
        else if (d.appMode === "any") {
          // One connected app is enough in "any" mode, so only complain when NONE is.
          if (slugs.every((slug) => appNeedsConnecting(slug, apps))) {
            for (const slug of slugs) need.add(slug);
          }
        } else {
          for (const slug of slugs) if (appNeedsConnecting(slug, apps)) need.add(slug);
        }
      }
    }
    return { apps: Array.from(need), needsPick };
  }, [graph, apps]);
  const missingText = useMemo(() => {
    const parts: string[] = [];
    if (missing.apps.length) {
      parts.push(
        t("workflows.setup.missing.connect", { apps: missing.apps.map(prettify).join(", ") }),
      );
    }
    if (missing.needsPick) parts.push(t("workflows.setup.missing.pickApp"));
    return parts.length ? `${t("workflows.setup.missing.prefix")} ${parts.join("; ")}.` : "";
  }, [missing, t]);

  // Apply an AI compose result: build the visual flow, replace triggers, auto-save.
  // `allowedAppsOverride` lets the plan phase persist the tools it decided are needed.
  const applyComposed = useCallback(
    async (res: ComposeRes, allowedAppsOverride?: string[]) => {
      if (!wf) return;
      const allowedApps =
        allowedAppsOverride && allowedAppsOverride.length
          ? allowedAppsOverride
          : (wf.allowedApps ?? []);
      const aiTriggers = Array.isArray(res.triggers) ? res.triggers : [];
      const steps = Array.isArray(res.steps) ? res.steps : [];
      const nextInstruction = res.instruction ?? instruction;
      const trigLabels = aiTriggers.length
        ? aiTriggers.map((tr) => labelForTrigger(t, tr))
        : triggerLabels.length
          ? triggerLabels
          : [t("workflows.setup.fromChatTrigger")];
      // buildGraphFromSteps can only emit a STRAIGHT LINE, so replacing a branching graph with
      // its output silently deletes the author's routes and loops. When the canvas branches and
      // the new plan does not, keep the canvas and say so — the rest of the compose result
      // (instruction, triggers, summary) still applies.
      const g = buildGraphFromSteps(trigLabels, steps, t, res.needsImage === true);
      const keptBranching = graphHasBranching(graph) && !graphHasBranching(g);
      if (keptBranching) {
        setBranchKeptNotice(true);
      } else {
        setGraph(g);
        setCanvasKey((k) => k + 1);
      }
      setInstruction(nextInstruction);
      setInstructionDirty(false);
      setSummary(res.summary ?? "");
      setQuestions(Array.isArray(res.questions) ? res.questions : []);

      await Promise.all([
        ...waRows.map((t) => postJson("/api/morrow/whatsapp-triggers/delete", { id: t.id })),
        ...wfRows.map((t) => postJson("/api/morrow/workflow-triggers/delete", { id: t.id })),
      ]);
      for (const t of aiTriggers) {
        if (t.kind === "whatsapp") {
          await postJson("/api/morrow/whatsapp-triggers", {
            name: `${wf.name} · My WhatsApp`,
            workflowId: wf.id,
            matchKind: t.matchKind ?? "all",
            matchValue: t.matchValue ?? "",
          });
        } else if (t.kind === "comms" && t.channel) {
          await postJson("/api/morrow/workflow-triggers", {
            workflowId: wf.id,
            kind: "comms",
            channel: t.channel,
            matchKind: t.matchKind ?? "all",
            matchValue: t.matchValue ?? "",
          });
        } else if (t.kind === "app-event" && t.toolkit && t.triggerSlug) {
          await postJson("/api/morrow/triggers/enable", {
            userId,
            toolkit: t.toolkit,
            triggerSlug: t.triggerSlug,
            config: {},
          });
          await postJson("/api/morrow/workflow-triggers", {
            workflowId: wf.id,
            kind: "app-event",
            toolkit: t.toolkit,
            triggerSlug: t.triggerSlug,
          });
        } else if (t.kind === "schedule" && t.cron) {
          await postJson("/api/morrow/workflow-triggers", {
            workflowId: wf.id,
            kind: "schedule",
            scheduleCron: t.cron,
          });
        } else if (t.kind === "vault-item-added") {
          // The AI proposes a folder by NAME (it has never seen a folder id), so match it to a
          // real folder. No match = no trigger: inventing a folder, or defaulting to the whole
          // vault, would run on every file the user ever adds.
          const folderId =
            t.vaultFolderId ??
            (await fetchVaultFolders()).find(
              (f) => f.name.toLowerCase() === (t.vaultFolderName ?? "").trim().toLowerCase(),
            )?.id;
          if (folderId) {
            await postJson("/api/morrow/workflow-triggers", {
              workflowId: wf.id,
              kind: "vault-item-added",
              vaultFolderId: folderId,
            });
          }
        }
      }
      await postJson("/api/morrow/workflows/update", {
        id: wf.id,
        name: wf.name,
        instruction: nextInstruction.trim(),
        allowedApps,
        capabilities: capsForGraph(g, wf.capabilities),
        autonomy: wf.autonomy ?? "approval",
        graph: g,
        enabled: wf.enabled,
      });
      void load();
    },
    [wf, instruction, triggerLabels, waRows, wfRows, userId, load, t],
  );

  const draft = useCallback(async () => {
    if (!wf) return;
    if (!intent.trim()) {
      setToast(t("workflows.setup.toast.typeIntent"));
      setTimeout(() => setToast(null), 2500);
      return;
    }
    setDrafting(true);
    const res = await postJson<ComposeRes>("/api/morrow/workflows/compose", {
      name: wf.name,
      intent,
      apps: wf.allowedApps ?? [],
      capabilities: capList,
      triggers: triggerLabels,
    });
    setDrafting(false);
    if (!res || (!res.instruction && !(res.steps && res.steps.length))) {
      setToast(t("workflows.setup.toast.couldNotBuild"));
      setTimeout(() => setToast(null), 3500);
      return;
    }
    await applyComposed(res);
    setToast(t("workflows.setup.toast.builtEndToEnd"));
    setTimeout(() => setToast(null), 3500);
  }, [wf, intent, capList, triggerLabels, applyComposed, t]);

  // Freshly-created workflow (just a title + description, no graph yet): PLAN it first
  // — reason about + clean the rough description into a reviewable plan (goal, tools,
  // trigger, outline). The user approves, then we BUILD from the CLEANED intent. We no
  // longer build outright from the raw description (that produced weak instructions).
  const plannedRef = useRef(false);
  useEffect(() => {
    if (!wf || plannedRef.current) return;
    const hasGraph = (graph?.nodes?.length ?? 0) > 0;
    const seed = (wf.instruction ?? "").trim();
    if (hasGraph || seed.length === 0) return;
    plannedRef.current = true;
    setPlanIntent(seed);
    void (async () => {
      setPlanning(true);
      const res = await postJson<WfPlan>("/api/morrow/workflows/plan", {
        name: wf.name,
        intent: seed,
        apps: wf.allowedApps ?? [],
        capabilities: capList,
        triggers: triggerLabels,
      });
      setPlanning(false);
      if (res && (res.understanding ?? "").trim()) {
        setPlan(res);
        setPlanChat([
          {
            role: "assistant",
            content:
              (res.reply ?? "").trim() ||
              "Here's my plan — tell me any changes or answer the questions, then build.",
          },
        ]);
      }
    })();
  }, [wf, graph, capList, triggerLabels]);

  // Chat about the plan: send the user's message, replay the whole exchange so the model
  // keeps context (it can ask follow-ups), and update the plan + append its reply.
  const sendPlanMessage = useCallback(async () => {
    if (!wf || planning) return;
    const note = planInput.trim();
    if (!note) return;
    const nextChat = [...planChat, { role: "user" as const, content: note }];
    setPlanChat(nextChat);
    setPlanInput("");
    setPlanning(true);
    const conversation = [
      { role: "user" as const, content: `Plan this: ${planIntent}` },
      ...nextChat,
    ];
    const res = await postJson<WfPlan>("/api/morrow/workflows/plan", {
      name: wf.name,
      intent: planIntent,
      apps: wf.allowedApps ?? [],
      capabilities: capList,
      triggers: triggerLabels,
      messages: conversation,
    });
    setPlanning(false);
    if (res && (res.understanding ?? "").trim()) {
      setPlan(res);
      setPlanChat([
        ...nextChat,
        { role: "assistant" as const, content: (res.reply ?? "").trim() || "Updated the plan." },
      ]);
    } else {
      setPlanChat([
        ...nextChat,
        { role: "assistant" as const, content: "I couldn't update just now — try again." },
      ]);
    }
  }, [wf, planning, planInput, planChat, planIntent, capList, triggerLabels]);

  // Approve the plan → BUILD the flow from the cleaned intent + the tools it chose,
  // then land on the finished flow (edit view). This replaces building outright.
  const approveAndBuild = useCallback(async () => {
    if (!wf || !plan) return;
    setBuilding(true);
    const chosenApps = plan.apps && plan.apps.length ? plan.apps : (wf.allowedApps ?? []);
    const res = await postJson<ComposeRes>("/api/morrow/workflows/compose", {
      name: wf.name,
      intent: (plan.understanding ?? "").trim() || (wf.instruction ?? ""),
      apps: chosenApps,
      capabilities: plan.capabilities && plan.capabilities.length ? plan.capabilities : capList,
      triggers: triggerLabels,
    });
    if (res && (res.instruction || (res.steps && res.steps.length))) {
      await applyComposed(res, chosenApps);
      setTab("edit");
      setToast(t("workflows.setup.toast.builtLive"));
      setTimeout(() => setToast(null), 4000);
    } else {
      setToast(t("workflows.setup.toast.couldNotBuildShort"));
      setTimeout(() => setToast(null), 3500);
    }
    setBuilding(false);
    setPlan(null);
  }, [wf, plan, capList, triggerLabels, applyComposed, t]);

  // Order the non-trigger nodes as a pipeline: follow edges from the triggers; fall back
  // to left→right (x) ordering for anything not reachable.
  const orderedSteps = useCallback((g: WfGraph): Node<WfNodeData>[] => {
    const byId = new Map(g.nodes.map((n) => [n.id, n]));
    const out: Node<WfNodeData>[] = [];
    const seen = new Set<string>();
    const nexts = (id: string) => g.edges.filter((e) => e.source === id).map((e) => e.target);
    const triggers = g.nodes.filter((n) => (n.data as WfNodeData).kind === "trigger");
    const queue = triggers.flatMap((t) => nexts(t.id));
    while (queue.length) {
      const id = queue.shift() as string;
      if (seen.has(id)) continue;
      const node = byId.get(id);
      if (!node) continue;
      seen.add(id);
      if ((node.data as WfNodeData).kind !== "trigger") out.push(node);
      queue.push(...nexts(id));
    }
    // Any orphan steps (not edge-reachable) appended by x position.
    const orphans = g.nodes
      .filter((n) => (n.data as WfNodeData).kind !== "trigger" && !seen.has(n.id))
      .sort((a, b) => a.position.x - b.position.x);
    return [...out, ...orphans];
  }, []);

  // Describe the current canvas flow as text, so the chat AI edits it in place. Routes and loops
  // are spelled out: the model REPLACES the whole graph with its answer, so anything left unsaid
  // here is deleted.
  const flowToText = useCallback(
    (): string => describeFlow(graph, orderedSteps(graph ?? { nodes: [], edges: [] })),
    [graph, orderedSteps],
  );

  // Compile the visual graph into the executable instruction the agent runs each time.
  // The agent processes steps in order, carrying each step's RESULT into the next, judges
  // Decision steps (stopping the flow if false), and honors the send channel + autonomy.
  const channelPhrase = (c?: string): string =>
    c === "personal-whatsapp"
      ? "the user's personal WhatsApp"
      : c === "whatsapp"
        ? "WhatsApp (Business)"
        : c === "telegram"
          ? "Telegram"
          : c === "slack"
            ? "Slack"
            : c === "discord"
              ? "Discord"
              : c === "chat"
                ? "the Morrow chat"
                : "the most appropriate channel";
  const buildExecInstruction = useCallback(
    (g: WfGraph | null, note: string): string => {
      if (!g || !g.nodes.length) return note.trim();
      const trigs = g.nodes.filter((n) => (n.data as WfNodeData).kind === "trigger");
      const steps = orderedSteps(g);
      const L: string[] = [];
      L.push(
        "Run this as an ordered, autonomous pipeline. Work through the steps IN ORDER; each step " +
          "operates on the RESULT of the step before it — carry that data forward and use it as the " +
          "input to the next step. Actually perform each step with your tools; do not just describe it. " +
          "You have autonomy at each step: decide whether it applies and whether to do it, adapt it, or " +
          "(only when clearly warranted) stop. DEFAULT TO ENGAGING: if the triggering message/event " +
          "plausibly relates to what this workflow handles, proceed — do NOT skip on doubt. " +
          "JUDGE INTENT FROM THE WHOLE CONVERSATION, not just this one message — you have the full " +
          "history with this person above. In an ONGOING conversation, treat short or vague follow-ups " +
          '("how much for this?", "this one?", "still available?", or even "hi"/"hello?") as ' +
          "continuing it — they QUALIFY; use the earlier messages (and any image they sent) to resolve " +
          'what "this/it" refers to. Only a NEW message with no relevant history that is CLEARLY ' +
          "unrelated should be skipped. ONLY stop when it is clearly unrelated to this workflow (purely " +
          "personal, off-topic, or spam) or a decision step's stated condition genuinely fails; then " +
          "take no action and reply with exactly [[SKIP]] (never send a generic/off-topic reply). " +
          "STAY GROUNDED — NEVER HALLUCINATE: base every answer strictly on the user's real documents " +
          "(via search_knowledge) and the actual conversation. Do NOT invent products, prices, specs, " +
          "availability, or facts. If the answer isn't in the documents, say you'll check rather than " +
          "guessing. Do exactly what the steps below define — nothing outside this workflow's scope.",
      );
      if (trigs.length) {
        L.push(
          `This runs when: ${trigs.map((t) => (t.data as WfNodeData).label).join("; ")}. ` +
            "The triggering event's content (the message, email, etc.) is the input to step 1.",
        );
      }
      steps.forEach((n, i) => {
        const d = n.data as WfNodeData;
        const num = `Step ${i + 1}`;
        if (d.kind === "app") {
          const slugs = nodeAppSlugs(d);
          const names = slugs.map((slug) =>
            slug === "personal-whatsapp" ? "the user's personal WhatsApp" : prettify(slug),
          );
          const appName =
            names.length === 0
              ? "the app"
              : names.length === 1
                ? names[0]!
                : `${names.slice(0, -1).join(", ")} ${d.appMode === "any" ? "or" : "and"} ${names.at(-1)!}`;
          const together =
            names.length > 1 ? (d.appMode === "any" ? " (whichever fits)" : " (all of them)") : "";
          const acts = (d.steps ?? []).filter(Boolean);
          L.push(
            `${num} — Using ${appName}${together}${slugs.length ? "" : " (pick the right one)"}: ${
              acts.length ? acts.join(", then ") : "do the needed action"
            }. Use the previous step's result as the input.`,
          );
        } else if (d.kind === "browser") {
          L.push(
            `${num} — In the user's OWN browser (their device, via the extension): ${d.instruction || d.text || "do the needed web task"}.`,
          );
        } else if (d.kind === "cloudBrowser") {
          L.push(
            `${num} — In a cloud browser (managed, always-on, no device needed): ${d.instruction || d.text || "do the needed web task"}.`,
          );
        } else if (d.kind === "computer") {
          L.push(
            `${num} — On a cloud computer (run code, call an API, or process files): ${d.instruction || d.text || "do the needed compute task"}.`,
          );
        } else if (d.kind === "vault") {
          const names = (d.vaultIds ?? [])
            .map((fid) => vaultItems.find((v) => v.id === fid)?.name)
            .filter(Boolean);
          L.push(
            `${num} — Look up the answer in the user's documents${
              names.length ? ` (${names.join(", ")})` : ""
            } with the search_knowledge tool — their content is indexed for search, including ` +
              `scanned/image PDFs (catalogues, price lists). GROUND EVERY answer strictly in what ` +
              `you find there: use the real product names, codes, prices, sizes, weights and ` +
              `materials from the documents. NEVER invent or guess prices, specs, or availability — ` +
              `if something isn't in the documents, say you'll check rather than making it up.`,
          );
        } else if (d.kind === "decision") {
          // A ROUTING step has named exits and maybe a loop; the single-exit prose below would
          // describe a different workflow from the one the node walker runs.
          const branchLines = branchInstructionLines(n, g, steps, String(num));
          if (branchLines.length > 0) {
            L.push(...branchLines);
            return;
          }
          L.push(
            `${num} — DECISION (this has teeth): judge the previous step's result and continue ` +
              `ONLY IF: ${d.text || "the condition holds"}. Judge it in the CONTEXT of the whole ` +
              `conversation, not just the latest message. Read this INCLUSIVELY — if it plausibly ` +
              `meets it (including a short follow-up in an ongoing chat), continue; only if it CLEARLY ` +
              `does not, STOP the whole workflow here — take no further action and reply with exactly ` +
              `[[SKIP]] (send nothing).`,
          );
        } else {
          // End node. Messaging is now explicit per mode (fixes the always-messaging default).
          const text = d.instruction || d.text || "deliver the result";
          const endMode = d.endMode ?? "message";
          if (endMode === "none") {
            L.push(`Final step — ${text}. Produce the result but do NOT message anyone.`);
          } else if (endMode === "save") {
            L.push(
              `Final step — ${text}. Save the result to the user's Vault; do NOT message anyone.`,
            );
          } else if (endMode === "webhook") {
            L.push(
              `Final step — ${text}. POST the result as JSON to this webhook: ${
                d.webhookUrl || "(no URL set)"
              }; do NOT message anyone.`,
            );
          } else {
            const auto = d.autonomous
              ? " Then keep working it: reply back and forth and stay engaged until it's resolved — do NOT stop after a single message."
              : "";
            L.push(`Final step — ${text}. Send it on ${channelPhrase(d.channel)}.${auto}`);
          }
        }
      });
      const trimmedNote = note.trim();
      // Only fold in a genuine EXTRA note. On re-save the `note` is the workflow's stored
      // instruction, which is ALREADY a compiled pipeline — folding that back in doubled
      // the instruction on every save (the "duplicated instruction" pile). Skip when the
      // note is itself a compiled instruction.
      if (trimmedNote && !trimmedNote.startsWith("Run this as an ordered")) {
        L.push(`Extra guidance: ${trimmedNote}`);
      }
      return L.join("\n");
    },
    [orderedSteps, vaultItems],
  );

  const sendChat = useCallback(
    async (message: string) => {
      if (!wf || !message.trim()) return;
      const nextMsgs = [...chatMessages, { role: "user" as const, content: message.trim() }];
      setChatMessages(nextMsgs);
      setChatInput("");
      setChatBusy(true);
      const res = await postJson<ComposeRes>("/api/morrow/workflows/compose", {
        name: wf.name,
        intent: message,
        apps: wf.allowedApps ?? [],
        capabilities: capList,
        triggers: triggerLabels,
        messages: nextMsgs,
        currentFlow: flowToText(),
      });
      setChatBusy(false);
      if (res && (res.instruction || (res.steps && res.steps.length))) {
        await applyComposed(res);
        // Recompute "what's missing" against the just-applied steps (state may lag).
        const stepApps = (res.steps ?? []).filter((s) => s.type === "app").map((s) => s.app ?? "");
        const need = stepApps.filter((a) => a && appNeedsConnecting(a, apps));
        const needsPick = stepApps.some((a) => !a);
        const note =
          need.length || needsPick
            ? ` To run it you still need to ${need.length ? `connect ${need.map(prettify).join(", ")}` : ""}${need.length && needsPick ? " and " : ""}${needsPick ? "pick an app for the empty steps" : ""} (Settings › Connections).`
            : "";
        setChatMessages((m) => [
          ...m,
          { role: "assistant", content: (res.reply || "Updated the flow.") + note },
        ]);
      } else {
        setChatMessages((m) => [
          ...m,
          { role: "assistant", content: "I couldn't update it just now — try rephrasing." },
        ]);
      }
    },
    [wf, chatMessages, capList, triggerLabels, apps, flowToText, applyComposed],
  );

  /**
   * Hand this workflow to someone else, from the page you are already on.
   *
   * Export existed only on the list page, as one of five flat text buttons on a card — findable
   * if you knew it was there. The place you want it is while you are looking at the workflow,
   * which is here. The server returns the whole file; the browser only has to save it.
   */
  const exportThis = useCallback(async () => {
    if (!wf) return;
    setExporting(true);
    const res = await getJson<{ export?: unknown }>(
      `/api/morrow/workflows/export?id=${encodeURIComponent(wf.id)}`,
    );
    setExporting(false);
    if (!res?.export) {
      setToast(t("workflows.export.failed"));
      return;
    }
    downloadWorkflowJson(wf.name, res.export);
    setToast(t("workflows.export.saved"));
  }, [wf, t]);

  const save = useCallback(async () => {
    if (!wf) return;
    setSaving(true);
    // If the user hand-edited the instruction, persist their EXACT text. Otherwise the
    // graph is the source of truth: compile it into the executable instruction (the
    // manual textarea folds in as extra guidance). Without this, a manual edit was
    // silently discarded on save — the edited text starts with "Run this as an ordered…"
    // so buildExecInstruction treated it as an already-compiled note and skipped it,
    // saving a fresh graph compile instead.
    const exec = buildExecInstruction(graph, instruction);
    const instructionToSave = instructionDirty
      ? instruction.trim()
      : exec.trim() || instruction.trim();
    // A browser/vault node on the canvas turns its capability on here, so an OLD workflow
    // heals on its next save (mirrors the server-side heal in the workflow update route).
    const caps = capsForGraph(graph, wf.capabilities);
    await postJson("/api/morrow/workflows/update", {
      id: wf.id,
      name: wf.name,
      instruction: instructionToSave,
      allowedApps: wf.allowedApps ?? [],
      capabilities: caps,
      autonomy: wf.autonomy ?? "approval",
      graph,
      enabled: wf.enabled,
    });
    setWf((prev) => (prev ? { ...prev, capabilities: caps } : prev));
    setInstructionDirty(false);
    setSaving(false);
    setToast(t("workflows.setup.toast.saved"));
    setTimeout(() => setToast(null), 2500);
  }, [wf, instruction, instructionDirty, graph, buildExecInstruction, t]);

  // Pause / resume this workflow — the narrow /set-enabled route, optimistic with a revert.
  const toggleEnabled = useCallback(async () => {
    if (!wf) return;
    const next = !wf.enabled;
    setWf({ ...wf, enabled: next });
    const res = await postJson<{ enabled?: boolean }>("/api/morrow/workflows/set-enabled", {
      id: wf.id,
      enabled: next,
    });
    if (!res) {
      setWf((w) => (w ? { ...w, enabled: !next } : w));
      setToast(t("workflows.toggle.failed"));
      setTimeout(() => setToast(null), 3000);
    }
  }, [wf, t]);

  const testRun = useCallback(async () => {
    if (!wf) return;
    const exec = buildExecInstruction(graph, instruction);
    // Respect a manual edit the same way Save does — run exactly what's shown.
    const instructionToSave = instructionDirty ? instruction.trim() : exec.trim();
    if (!instructionToSave) {
      setToast(t("workflows.setup.toast.buildFirst"));
      setTimeout(() => setToast(null), 2500);
      return;
    }
    setRunning(true);
    // Compile the graph → executable instruction, save it, then fire a one-off run.
    const caps = capsForGraph(graph, wf.capabilities);
    await postJson("/api/morrow/workflows/update", {
      id: wf.id,
      name: wf.name,
      instruction: instructionToSave,
      allowedApps: wf.allowedApps ?? [],
      capabilities: caps,
      autonomy: wf.autonomy ?? "approval",
      graph,
      enabled: wf.enabled,
    });
    const run = await postJsonResult<{ ok?: boolean }>("/api/morrow/workflows/run", { id: wf.id });
    setRunning(false);
    // Show it in the right panel's Runs tab — no need to leave the canvas.
    if (run.data?.ok) {
      setChatOpen(true);
      setRightTab("runs");
      setTimeout(() => void load(), 1500);
    } else {
      // A refusal the server explained (409 "already running") says exactly what
      // happened; anything else falls back to the generic copy.
      setToast(run.error ?? t("workflows.setup.toast.couldNotRun"));
      setTimeout(() => setToast(null), 3000);
    }
  }, [wf, instruction, instructionDirty, graph, load, buildExecInstruction, t]);

  if (notFound) {
    return (
      <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-4 px-4 py-8">
        <Link to="/workflows" className={`${GHOST} w-fit`}>
          <ArrowLeftIcon className="size-4" /> {t("workflows.setup.back")}
        </Link>
        <p className="text-sm text-muted-foreground">{t("workflows.setup.notFound")}</p>
      </div>
    );
  }
  if (!wf) {
    // Builder-shaped shimmer (top bar + a few node placeholders) rather than a bare spinner, so
    // the skeleton matches what resolves into place.
    return (
      <div className="flex h-full w-full flex-col overflow-hidden" aria-busy aria-live="polite">
        <span className="sr-only">{t("workflows.setup.loadingWorkflow")}</span>
        <div className="flex items-center justify-between gap-2 p-3">
          <div className="h-8 w-44 animate-pulse rounded-md bg-muted" />
          <div className="flex items-center gap-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-8 w-20 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        </div>
        <div className="flex flex-1 items-center gap-8 p-10">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-8">
              <div className="h-32 w-64 animate-pulse rounded-xl bg-muted" />
              {i < 2 ? <div className="h-0.5 w-10 animate-pulse rounded bg-muted/60" /> : null}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Freshly-created workflow (no flow yet): reason → review → build, shown CENTERED
  // before the canvas exists. Replaces building outright from the raw description.
  if ((graph?.nodes?.length ?? 0) === 0 && (planning || building || plan)) {
    return (
      <div className="flex h-full w-full flex-col overflow-hidden">
        {planning || building ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center text-muted-foreground">
            <Loader2Icon className="size-7 animate-spin" />
            <div className="text-sm font-medium text-foreground">
              {building
                ? t("workflows.setup.plan.buildingTitle")
                : t("workflows.setup.plan.understandingTitle")}
            </div>
            <p className="max-w-sm text-[13px]">
              {building
                ? t("workflows.setup.plan.buildingBody")
                : t("workflows.setup.plan.understandingBody")}
            </p>
          </div>
        ) : plan ? (
          <div className="flex h-full w-full flex-col gap-4 overflow-hidden p-4 sm:p-6 md:flex-row">
            <div className="min-w-0 flex-1 overflow-y-auto text-left md:pr-2">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("workflows.setup.plan.heresThePlan")}
              </div>
              <h1 className="text-xl font-semibold text-foreground text-balance">
                {plan.goal || wf.name}
              </h1>
              {plan.understanding ? (
                <p className="mt-2 text-sm text-muted-foreground">{plan.understanding}</p>
              ) : null}
              {plan.triggerSummary ? (
                <div className="mt-4">
                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t("workflows.setup.plan.startsWhen")}
                  </div>
                  <p className="text-[13px] text-foreground">{plan.triggerSummary}</p>
                </div>
              ) : null}
              {(plan.apps && plan.apps.length) ||
              (plan.capabilities && plan.capabilities.length) ? (
                <div className="mt-4">
                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t("workflows.setup.plan.tools")}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(plan.apps ?? []).map((a) => (
                      <span
                        key={a}
                        className="rounded-md border border-border/60 bg-muted/30 px-2 py-0.5 text-[12px] text-foreground"
                      >
                        {prettify(a)}
                      </span>
                    ))}
                    {(plan.capabilities ?? []).map((c) => (
                      <span
                        key={c}
                        className="rounded-md border border-border/60 bg-muted/30 px-2 py-0.5 text-[12px] text-muted-foreground"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
              {plan.outline && plan.outline.length ? (
                <div className="mt-4">
                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t("workflows.setup.plan.howItWorks")}
                  </div>
                  <ol className="flex list-decimal flex-col gap-1 pl-5 text-[13px] text-muted-foreground">
                    {plan.outline.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ol>
                </div>
              ) : null}
              {plan.questions && plan.questions.length ? (
                <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                    {t("workflows.setup.plan.worthClarifying")}
                  </div>
                  <ul className="flex list-disc flex-col gap-1 pl-5 text-[13px] text-foreground">
                    {plan.questions.map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                  </ul>
                  <p className="mt-1.5 text-[12px] text-muted-foreground">
                    {t("workflows.setup.plan.clarifyHint")}
                  </p>
                </div>
              ) : null}
            </div>
            {/* RIGHT: talk to it — ask for changes, answer its questions, keep refining. */}
            <div className="flex min-h-0 w-full shrink-0 flex-col overflow-hidden rounded-lg border border-border/60 md:h-full md:w-[380px]">
              <div className="border-b border-border/60 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("workflows.setup.plan.chatTitle")}
              </div>
              <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-3">
                {planChat.map((m, i) => (
                  <div
                    key={i}
                    className={
                      m.role === "user"
                        ? "self-end max-w-[85%] rounded-lg bg-primary px-3 py-1.5 text-[13px] text-primary-foreground"
                        : "self-start max-w-[85%] rounded-lg border border-border/60 bg-muted/30 px-3 py-1.5 text-[13px] text-foreground"
                    }
                  >
                    {m.content}
                  </div>
                ))}
                {planning ? (
                  <div className="flex items-center gap-1.5 self-start text-[12px] text-muted-foreground">
                    <Loader2Icon className="size-3.5 animate-spin" />{" "}
                    {t("workflows.setup.plan.thinking")}
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-2 border-t border-border/60 p-2">
                <input
                  className={INPUT}
                  placeholder={t("workflows.setup.plan.inputPlaceholder")}
                  value={planInput}
                  onChange={(e) => setPlanInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void sendPlanMessage();
                    }
                  }}
                />
                <button
                  type="button"
                  className={BTN}
                  onClick={() => void sendPlanMessage()}
                  disabled={!planInput.trim() || planning}
                >
                  {t("workflows.setup.send")}
                </button>
              </div>
              <div className="flex items-center gap-2 border-t border-border/60 p-2">
                <button type="button" className={BTN} onClick={() => void approveAndBuild()}>
                  {t("workflows.setup.buildWorkflow")}
                </button>
                <button
                  type="button"
                  className={GHOST}
                  onClick={() => {
                    setPlan(null);
                    setTab("edit");
                  }}
                >
                  {t("workflows.setup.skipManual")}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={
        tab === "edit"
          ? "flex h-full w-full flex-col overflow-hidden"
          : "mx-auto flex h-full w-full max-w-3xl flex-col gap-5 overflow-y-auto px-4 py-8 sm:px-6"
      }
    >
      {tab === "overview" ? (
        <>
          <div className="flex items-center justify-between gap-3">
            <Link to="/workflows" className={`${GHOST} w-fit`}>
              <ArrowLeftIcon className="size-4" /> {t("workflows.setup.back")}
            </Link>
            <div className="flex items-center gap-2">
              {toast ? (
                <span className="rounded-md border border-border/60 bg-muted/40 px-3 py-1.5 text-xs text-foreground">
                  {toast}
                </span>
              ) : null}
              <button type="button" className={BTN} onClick={() => setTab("edit")}>
                {t("workflows.setup.edit")}
              </button>
            </div>
          </div>

          <div>
            <h1 className="text-xl font-semibold text-foreground">{wf.name}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("workflows.setup.overviewHint")}
            </p>
          </div>

          {/* Dashboard: what it's wired to + the run flow */}
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-border/60 p-3">
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("workflows.setup.triggers")}
              </div>
              {triggerLabels.length ? (
                <ul className="flex flex-col gap-1 text-[13px] text-muted-foreground">
                  {triggerLabels.map((l) => (
                    <li key={l} className="truncate">
                      {l}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[12px] text-muted-foreground/70">
                  {t("workflows.setup.runsFromChat")}
                </p>
              )}
            </div>
            <div className="rounded-lg border border-border/60 p-3">
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("workflows.setup.apps")}
              </div>
              <p className="text-[13px] text-muted-foreground">
                {wf.allowedApps && wf.allowedApps.length
                  ? wf.allowedApps.map(prettify).join(", ")
                  : t("workflows.setup.allConnectedApps")}
              </p>
            </div>
            <div className="rounded-lg border border-border/60 p-3">
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("workflows.setup.capabilities")}
              </div>
              <p className="text-[13px] text-muted-foreground">
                {capListDisplay.length ? capListDisplay.join(", ") : t("workflows.setup.appsOnly")}{" "}
                · {wf.autonomy ?? "approval"}
              </p>
            </div>
          </div>

          {/* Runs — the flow */}
          <div className="flex flex-col gap-2 rounded-xl border border-border/60 p-4">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("workflows.setup.runs")} {runs.length ? `(${runs.length})` : ""}
              </div>
              <button
                type="button"
                className={GHOST}
                onClick={() => void testRun()}
                disabled={running}
              >
                {running ? (
                  <Loader2Icon className="size-4 animate-spin" />
                ) : (
                  <PlayIcon className="size-4" />
                )}
                {t("workflows.setup.testRun")}
              </button>
            </div>
            {runs.length === 0 ? (
              <div className="flex flex-col gap-2">
                <p className="text-[12px] text-muted-foreground/70">
                  {t("workflows.setup.noRunsYet")}
                </p>
                <ol className="relative ml-1.5 flex flex-col gap-3 border-l border-dashed border-border/60 pl-4 opacity-60">
                  {[
                    { s: "whatsapp", d: "Jane Doe · pricing question", ok: true, t: "just now" },
                    { s: "app-event", d: "gmail · new_email", ok: true, t: "2h ago" },
                    {
                      s: "schedule",
                      d: "0 8 * * *",
                      ok: false,
                      t: "yesterday",
                      e: "app not connected",
                    },
                  ].map((r, i) => (
                    <li key={i} className="relative">
                      <span
                        className={`absolute -left-[1.3rem] top-1 size-2.5 rounded-full ring-4 ring-background ${
                          r.ok ? "bg-emerald-500" : "bg-red-500"
                        }`}
                      />
                      <div className="flex items-center justify-between gap-3 text-[13px]">
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="font-medium capitalize text-foreground">{r.s}</span>
                          <span className="truncate text-muted-foreground">· {r.d}</span>
                        </span>
                        <span className="shrink-0 text-[11px] text-muted-foreground/70">{r.t}</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground/80">
                        {r.ok
                          ? t("workflows.setup.runCompleted")
                          : t("workflows.setup.runFailed", { error: r.e ?? "" })}
                      </div>
                    </li>
                  ))}
                </ol>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground/50">
                  {t("workflows.setup.exampleRuns")}
                </p>
              </div>
            ) : (
              <ol className="relative ml-1.5 flex flex-col gap-3 border-l border-border/60 pl-4">
                {runs.map((r) => (
                  <li key={r.id} className="relative">
                    <span
                      className={`absolute -left-[1.3rem] top-1 size-2.5 rounded-full ring-4 ring-background ${
                        r.status === "running"
                          ? "bg-primary"
                          : r.status === "ok"
                            ? "bg-emerald-500"
                            : r.status === "skipped"
                              ? "bg-muted-foreground/50"
                              : "bg-red-500"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setSelectedRunId((cur) => (cur === r.id ? null : r.id))}
                      className="w-full text-left"
                    >
                      <div className="flex items-center justify-between gap-3 text-[13px]">
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="font-medium capitalize text-foreground">{r.source}</span>
                          {runDetailLabel(r.detail, t) ? (
                            <span className="truncate text-muted-foreground">
                              · {runDetailLabel(r.detail, t)}
                            </span>
                          ) : null}
                        </span>
                        <span className="shrink-0 text-[11px] text-muted-foreground/70">
                          {new Date(r.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground/80">
                        {r.status === "running"
                          ? "Running…"
                          : r.status === "ok"
                            ? t("workflows.setup.runCompleted")
                            : r.error
                              ? t("workflows.setup.runFailed", { error: r.error })
                              : t("workflows.setup.runFailedShort")}
                      </div>
                    </button>
                    {selectedRunId === r.id ? (
                      <RunStepsPanel
                        runId={r.id}
                        graph={graph}
                        runStatus={r.status}
                        threadId={r.threadId}
                      />
                    ) : null}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </>
      ) : (
        <div className="relative flex-1 overflow-hidden">
          {/* Full-bleed canvas fills the whole area */}
          <div className="absolute inset-0">
            <WorkflowCanvas
              key={canvasKey}
              initial={graph}
              seedTriggerLabels={triggerLabels}
              apps={apps}
              vaultItems={vaultItems}
              onEditTriggers={() => setPanel("triggers")}
              onChange={setGraph}
            />
          </div>

          {/* Floating top bar — nav, Build-with-AI, actions (canvas shows through gaps) */}
          <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex flex-wrap items-center gap-2 p-3">
            <button
              type="button"
              onClick={() => setTab("overview")}
              className="pointer-events-auto inline-flex h-7 max-w-[40vw] items-center justify-center gap-1.5 rounded-md border border-border/60 bg-background/90 px-2.5 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur transition hover:bg-muted/50 hover:text-foreground"
            >
              <ArrowLeftIcon className="size-3.5" />
              <span className="truncate">{wf.name}</span>
            </button>

            <div className="pointer-events-auto ml-auto flex items-center gap-2">
              {toast ? (
                <span className="rounded-md border border-border/60 bg-background/90 px-3 py-1.5 text-xs text-foreground shadow-sm backdrop-blur">
                  {toast}
                </span>
              ) : null}
              <button
                type="button"
                role="switch"
                aria-checked={wf.enabled}
                onClick={() => void toggleEnabled()}
                title={wf.enabled ? t("workflows.card.pause") : t("workflows.card.enable")}
                aria-label={wf.enabled ? t("workflows.card.pause") : t("workflows.card.enable")}
                className={`pointer-events-auto relative h-4 w-7 shrink-0 rounded-full shadow-sm transition ${wf.enabled ? "bg-primary" : "bg-muted"}`}
              >
                <span
                  className={`absolute top-0.5 size-3 rounded-full bg-background shadow transition-all ${wf.enabled ? "left-[14px]" : "left-0.5"}`}
                />
              </button>
              <button
                type="button"
                onClick={() => setChatOpen((o) => !o)}
                title={t("workflows.setup.chat")}
                aria-label={t("workflows.setup.chat")}
                className={`${ICON_ACTION} ${chatOpen ? "border-primary/50 text-primary" : ""}`}
              >
                <SparklesIcon className="size-3.5" />
              </button>
              <button
                type="button"
                onClick={() => void exportThis()}
                disabled={exporting}
                title={t("workflows.card.export")}
                aria-label={t("workflows.card.export")}
                className={ICON_ACTION}
              >
                {exporting ? (
                  <Loader2Icon className="size-3.5 animate-spin" />
                ) : (
                  <DownloadIcon className="size-3.5" />
                )}
              </button>
              <button
                type="button"
                onClick={() => void testRun()}
                disabled={running}
                title={t("workflows.setup.test")}
                aria-label={t("workflows.setup.test")}
                className={ICON_ACTION}
              >
                {running ? (
                  <Loader2Icon className="size-3.5 animate-spin" />
                ) : (
                  <PlayIcon className="size-3.5" />
                )}
              </button>
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving}
                className="pointer-events-auto inline-flex h-7 items-center justify-center gap-1.5 rounded-md border border-border bg-primary px-2.5 text-xs font-semibold text-primary-foreground shadow-sm transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {saving ? (
                  <Loader2Icon className="size-3.5 animate-spin" />
                ) : (
                  t("workflows.setup.save")
                )}
              </button>
              <button
                type="button"
                onClick={() => setTab("overview")}
                className="pointer-events-auto inline-flex h-7 items-center justify-center rounded-md border border-border/60 bg-background/90 px-2.5 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur transition hover:bg-muted/50 hover:text-foreground"
              >
                {t("workflows.setup.done")}
              </button>
            </div>
          </div>

          {/* What's still needed to make it run (unconnected apps / empty steps). */}
          {missingText ? (
            <div className="pointer-events-auto absolute left-1/2 top-16 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-[12px] text-amber-700 shadow-sm backdrop-blur dark:text-amber-300">
              <span>{missingText}</span>
              <Link to="/settings/connections" className="font-semibold underline hover:opacity-80">
                {t("workflows.setup.connect")}
              </Link>
            </div>
          ) : null}

          {/* An AI edit tried to flatten a branching canvas; we kept the canvas instead. Saying so
              matters — silently keeping it would look like the edit simply didn't work. */}
          {branchKeptNotice ? (
            <div className="pointer-events-auto absolute left-1/2 top-28 z-20 flex max-w-xl -translate-x-1/2 items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-700 shadow-sm backdrop-blur dark:text-amber-300">
              <span>{t("workflows.setup.branchKept")}</span>
              <button
                type="button"
                onClick={() => setBranchKeptNotice(false)}
                aria-label={t("workflows.canvas.close")}
                className="shrink-0 font-semibold hover:opacity-80"
              >
                <XIcon className="size-3.5" />
              </button>
            </div>
          ) : null}

          {/* Bottom-left: one pill per panel — WHAT STARTS IT, and WHAT IT DOES. */}
          {panel === "none" ? (
            <div className="pointer-events-auto absolute bottom-4 left-4 z-20 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPanel("triggers")}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3.5 py-2 text-[13px] font-semibold text-foreground shadow-md transition hover:bg-muted"
              >
                <ZapIcon className="size-3.5" />
                {t("workflows.setup.triggers")}
              </button>
              <button
                type="button"
                onClick={() => setPanel("instruction")}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3.5 py-2 text-[13px] font-semibold text-foreground shadow-md transition hover:bg-muted"
              >
                <FileTextIcon className="size-3.5" />
                {t("workflows.setup.instruction")}
                {questions.length ? (
                  <span className="inline-flex size-4 items-center justify-center rounded-full bg-amber-500/20 text-[10px] font-bold text-amber-600 dark:text-amber-400">
                    {questions.length}
                  </span>
                ) : null}
              </button>
            </div>
          ) : panel === "triggers" ? (
            <div className="pointer-events-auto absolute bottom-4 left-4 z-20 w-96 max-w-[calc(100%-2rem)] overflow-hidden rounded-xl border border-border bg-background shadow-2xl">
              <div className="flex items-center justify-between border-b border-border/60 px-3 py-2">
                <span className="text-[12px] font-semibold text-foreground">
                  {t("workflows.setup.triggers")}
                </span>
                <button
                  type="button"
                  onClick={() => setPanel("none")}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Close"
                >
                  <XIcon className="size-4" />
                </button>
              </div>
              <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto p-3">
                <div className="flex flex-col gap-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t("workflows.setup.triggers")}
                  </div>
                  {waRows.length + wfRows.length > 0 ? (
                    <ul className="flex flex-wrap gap-1.5">
                      {waRows.map((row) => (
                        <li
                          key={row.id}
                          className="flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/30 px-2 py-0.5 text-[11px]"
                        >
                          <span className="text-foreground/80">
                            {t("workflows.setup.myWhatsapp")} ·{" "}
                            {workflowMatchPhrase(row.matchKind, row.matchValue, t)}
                          </span>
                          <button
                            type="button"
                            onClick={() => void removeWa(row.id)}
                            className="text-muted-foreground hover:text-foreground"
                            aria-label="Remove"
                          >
                            <XIcon className="size-3" />
                          </button>
                        </li>
                      ))}
                      {wfRows.map((row) => (
                        <li
                          key={row.id}
                          className="flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/30 px-2 py-0.5 text-[11px]"
                        >
                          <span className="text-foreground/80">
                            {row.kind === "comms"
                              ? `${prettify(row.channel ?? "")} · ${workflowMatchPhrase(row.matchKind, row.matchValue, t)}`
                              : row.kind === "app-event"
                                ? `${prettify(row.toolkit ?? "")} · ${row.triggerSlug}`
                                : row.kind === "vault-item-added"
                                  ? vaultFolderLabel(t, vaultFolderNames[row.vaultFolderId ?? ""])
                                  : cronToHumanLocalized(row.scheduleCron ?? "", t)}
                          </span>
                          <button
                            type="button"
                            onClick={() => void removeWf(row.id)}
                            className="text-muted-foreground hover:text-foreground"
                            aria-label="Remove"
                          >
                            <XIcon className="size-3" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[11px] text-muted-foreground/70">
                      {t("workflows.setup.runsFromChatAdd")}
                    </p>
                  )}
                  <TriggerComposer
                    userId={userId}
                    onAdd={(p) => void addTrigger(p)}
                    hasWhatsapp={waRows.length > 0}
                    openOn={triggerSourceOf([
                      ...(waRows.length > 0 ? ["whatsapp"] : []),
                      ...wfRows.map((row) => row.kind),
                    ])}
                  />
                </div>
              </div>
            </div>
          ) : (
            /*
             * The instruction, on its own. It's the longest thing on the page and the one most
             * often rewritten, so it gets the full height of the canvas instead of a tall textarea
             * inside a scroll box. The clarifying questions live here too — they're questions about
             * THIS text, and the answer is to edit it.
             */
            <div className="pointer-events-auto absolute bottom-4 left-4 top-16 z-20 flex w-[28rem] max-w-[calc(100%-2rem)] flex-col overflow-hidden rounded-xl border border-border bg-background shadow-2xl">
              <div className="flex items-center justify-between border-b border-border/60 px-3 py-2">
                <span className="text-[12px] font-semibold text-foreground">
                  {t("workflows.setup.instruction")}
                </span>
                <button
                  type="button"
                  onClick={() => setPanel("none")}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Close"
                >
                  <XIcon className="size-4" />
                </button>
              </div>
              <div className="flex min-h-0 flex-1 flex-col gap-2 p-3">
                {summary ? (
                  <div className="rounded-md border border-primary/30 bg-primary/5 p-2 text-[11px] text-foreground">
                    {summary}
                  </div>
                ) : null}
                {questions.length ? (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2">
                    <div className="mb-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                      {t("workflows.setup.clarifyBuild")}
                    </div>
                    <ul className="list-disc pl-4 text-[11px] text-foreground/90">
                      {questions.map((q) => (
                        <li key={q}>{q}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <textarea
                  className={`${INPUT} min-h-0 flex-1 resize-none whitespace-pre-wrap text-[12px] leading-relaxed`}
                  placeholder={t("workflows.setup.instructionPlaceholder")}
                  value={instruction}
                  onChange={(e) => {
                    setInstruction(e.target.value);
                    setInstructionDirty(true);
                  }}
                />
              </div>
            </div>
          )}

          {/* Right-side chat — talk through changes; the AI edits the flow in place. */}
          {chatOpen ? (
            <div className="pointer-events-auto absolute bottom-3 right-3 top-16 z-20 flex w-80 max-w-[calc(100%-1.5rem)] flex-col overflow-hidden rounded-xl border border-border bg-background shadow-2xl">
              <div className="flex items-center justify-between border-b border-border/60 px-2 py-1.5">
                <div className="flex items-center gap-1">
                  {(
                    [
                      ["chat", t("workflows.setup.editWithAi")],
                      [
                        "runs",
                        runs.length
                          ? t("workflows.setup.runsTabCount", { count: runs.length })
                          : t("workflows.setup.runsTab"),
                      ],
                    ] as ReadonlyArray<["chat" | "runs", string]>
                  ).map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setRightTab(val)}
                      className={`rounded-md px-2 py-1 text-[12px] font-semibold transition ${
                        rightTab === val
                          ? "bg-muted text-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setChatOpen(false)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Close"
                >
                  <XIcon className="size-4" />
                </button>
              </div>

              {rightTab === "chat" ? (
                <>
                  <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-3">
                    {chatMessages.length === 0 ? (
                      <p className="text-[12px] text-muted-foreground/70">
                        {t("workflows.setup.chatEmpty")}
                      </p>
                    ) : null}
                    {chatMessages.map((m, i) => (
                      <div
                        key={i}
                        className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-[12px] ${
                          m.role === "user"
                            ? "self-end bg-primary text-primary-foreground"
                            : "self-start bg-muted text-foreground"
                        }`}
                      >
                        {m.content}
                      </div>
                    ))}
                    {chatBusy ? (
                      <div className="flex items-center gap-1.5 self-start text-[12px] text-muted-foreground">
                        <Loader2Icon className="size-3.5 animate-spin" />{" "}
                        {t("workflows.setup.chatWorking")}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-1.5 border-t border-border/60 p-2">
                    <input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && chatInput.trim()) void sendChat(chatInput);
                      }}
                      placeholder={t("workflows.setup.chatAiPlaceholder")}
                      className="flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-[12px] text-foreground outline-none focus:border-primary/50"
                    />
                    <button
                      type="button"
                      onClick={() => void sendChat(chatInput)}
                      disabled={chatBusy || !chatInput.trim()}
                      className="rounded-md bg-primary px-2.5 py-1.5 text-[12px] font-semibold text-primary-foreground disabled:opacity-60"
                    >
                      {t("workflows.setup.send")}
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex-1 overflow-y-auto p-3">
                  {runs.length === 0 ? (
                    <p className="text-[12px] text-muted-foreground/70">
                      {t("workflows.setup.noRunsChat")}
                    </p>
                  ) : (
                    <ol className="relative ml-1 flex flex-col gap-3 border-l border-border/60 pl-4">
                      {runs.map((r) => (
                        <li key={r.id} className="relative">
                          <span
                            className={`absolute -left-[1.15rem] top-1 size-2 rounded-full ring-4 ring-background ${
                              r.status === "running"
                                ? "bg-primary"
                                : r.status === "ok"
                                  ? "bg-emerald-500"
                                  : r.status === "skipped"
                                    ? "bg-muted-foreground/50"
                                    : "bg-red-500"
                            }`}
                          />
                          <button
                            type="button"
                            onClick={() => setSelectedRunId((cur) => (cur === r.id ? null : r.id))}
                            className="w-full text-left"
                          >
                            <div className="flex min-w-0 items-center gap-1 text-[12px]">
                              <span className="shrink-0 font-medium capitalize text-foreground">
                                {r.source}
                              </span>
                              {r.detail ? (
                                <span className="truncate text-muted-foreground">
                                  {" "}
                                  · {r.detail}
                                </span>
                              ) : null}
                            </div>
                            <div className="truncate text-[10px] text-muted-foreground/70">
                              {new Date(r.createdAt).toLocaleString()} ·{" "}
                              {r.status === "running"
                                ? "Running…"
                                : r.status === "ok"
                                  ? "Completed"
                                  : r.status === "skipped"
                                    ? "Skipped"
                                    : `Failed${r.error ? ` — ${r.error}` : ""}`}
                            </div>
                          </button>
                          {selectedRunId === r.id ? (
                            <RunStepsPanel
                              runId={r.id}
                              graph={graph}
                              runStatus={r.status}
                              threadId={r.threadId}
                            />
                          ) : null}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
